import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Body,
  Chip,
  Button,
  Heading,
  MaterialUICore,
  MaterialIcons,
  IconButton,
} from '@iclinic/design-system';

import { monetaryFormatter } from 'shared/utils/formatters';
import { getSummaryData } from '../../utils/summary';
import {
  getSelectedPlan,
  getSelectedAddons,
  getPhysicianByAddon,
  getPhysicians,
  getCouponCode,
  getAppliedVoucher,
  getVoucherErrorMessage,
  getIsVoucher,
  getIsVoucherOrCouponApplied,
} from '../../state/selectors';
import * as S from './MobileFooterSummary.styles';
import {
  applyVoucher,
  applyCouponSuccess,
  removeVoucher,
  setAppliedVoucher,
  setIsVoucherOrCouponApplied,
} from 'features/checkout/state/reducer';
import VoucherInput from './VoucherInput';
import {
  saveIsVoucherApplied,
  loadIsVoucherApplied,
} from 'features/checkout/utils';

interface MobileFooterSummaryProps {
  isAnnual: boolean;
  onPaymentClick?: () => void;
}

const { Collapse } = MaterialUICore;

const { KeyboardArrowDown, KeyboardArrowUpIcon, WarningAmberOutlined } =
  MaterialIcons;

const MobileFooterSummary = ({
  isAnnual,
  onPaymentClick,
}: MobileFooterSummaryProps) => {
  const dispatch = useDispatch();
  const selectedAddons = useSelector(getSelectedAddons);
  const selectedPlanMobile = useSelector(getSelectedPlan);
  const physicians = useSelector(getPhysicians);
  const physicianByAddon = useSelector(getPhysicianByAddon);
  const couponDataMobile = useSelector(getCouponCode);
  const appliedVoucherMobile = useSelector(getAppliedVoucher);
  const voucherErrorMessageMobile = useSelector(getVoucherErrorMessage);
  const isVoucherMobile = useSelector(getIsVoucher);
  const isVoucherOrCouponAppliedMobile = useSelector(
    getIsVoucherOrCouponApplied,
  );

  const [isVoucherMobileVisible, setIsVoucherMobileVisible] = useState(false);
  const [showRemoveMobileAlert, setShowRemoveMobileAlert] = useState(false);
  const [codeValueMobile, setCodeValueMobile] = useState('');

  const { totalValue, discountValue, percentageValue } = getSummaryData({
    isAnnual,
    addons: selectedAddons,
    plan: selectedPlanMobile,
    physicians,
    physicianByAddon,
    coupon: couponDataMobile,
  });

  const toggleVoucherVisible = () => {
    setIsVoucherMobileVisible(!isVoucherMobileVisible);
  };

  const handleApplyMobileVoucher = () => {
    if (codeValueMobile.trim()) {
      saveIsVoucherApplied('true');
      dispatch(setAppliedVoucher('true'));
      dispatch(applyVoucher({ voucher: codeValueMobile }));
    }
  };

  const handleChangeMobile = (value: string) => {
    setCodeValueMobile(value);
  };

  const handleRemoveMobileVoucher = () => {
    setShowRemoveMobileAlert(true);
  };

  const confirmRemoveMobileVoucher = () => {
    dispatch(removeVoucher({ voucher: codeValueMobile }));
    setCodeValueMobile('');
    setShowRemoveMobileAlert(false);
  };

  useEffect(() => {
    if (couponDataMobile?.code !== null) {
      const savedVoucher = loadIsVoucherApplied();
      if (savedVoucher) {
        dispatch(setAppliedVoucher(savedVoucher));
      }
    }

    if (couponDataMobile?.code) {
      dispatch(setIsVoucherOrCouponApplied(true));
      setCodeValueMobile(couponDataMobile.code);
      dispatch(applyCouponSuccess(couponDataMobile.code));
    }
  }, [couponDataMobile, dispatch]);

  useEffect(() => {
    if (!couponDataMobile?.code) {
      setCodeValueMobile(appliedVoucherMobile ?? '');
    }
  }, [appliedVoucherMobile, couponDataMobile]);

  return (
    <S.MobileCard>
      <S.VoucherToggleMobile>
        <Body variant="sm">Cupom de desconto</Body>
        <IconButton
          label="Alterna visibilidade do cupom"
          tooltip="none"
          onClick={toggleVoucherVisible}
        >
          {isVoucherMobileVisible ? (
            <KeyboardArrowUpIcon />
          ) : (
            <KeyboardArrowDown />
          )}
        </IconButton>
      </S.VoucherToggleMobile>

      <Collapse in={isVoucherMobileVisible}>
        <S.VoucherCollapseWrapperMobile>
          <VoucherInput
            value={codeValueMobile}
            isApplied={isVoucherOrCouponAppliedMobile}
            error={voucherErrorMessageMobile}
            onApply={handleApplyMobileVoucher}
            onRemove={handleRemoveMobileVoucher}
            onChange={handleChangeMobile}
            isVoucher={isVoucherMobile}
          />
        </S.VoucherCollapseWrapperMobile>
      </Collapse>

      {showRemoveMobileAlert && (
        <S.WarningRemoveVoucherMobile>
          <S.WarningContentMobile>
            <WarningAmberOutlined />
            <S.Body variant="xs">
              Tem certeza que deseja remover o desconto?
            </S.Body>
          </S.WarningContentMobile>
          <S.WarningButtonMobile>
            <S.Button
              variant="text"
              size="small"
              onClick={() => setShowRemoveMobileAlert(false)}
            >
              Cancelar
            </S.Button>
            <S.Button
              variant="text"
              size="small"
              onClick={confirmRemoveMobileVoucher}
            >
              Remover
            </S.Button>
          </S.WarningButtonMobile>
        </S.WarningRemoveVoucherMobile>
      )}

      {discountValue &&
        codeValueMobile &&
        isVoucherOrCouponAppliedMobile &&
        !voucherErrorMessageMobile && (
          <S.ChipWrapper>
            <Chip
              label={`Cupom de ${percentageValue || ''}% aplicado`}
              severity="success"
              variant="default"
              size="small"
            />
            <Heading variant="xs">
              - R$ {monetaryFormatter(discountValue)}
            </Heading>
          </S.ChipWrapper>
        )}

      {!discountValue &&
        codeValueMobile &&
        appliedVoucherMobile &&
        !voucherErrorMessageMobile && (
          <S.ChipWrapper>
            <S.Chip
              label="Cupom indisponível para este plano ou frequência"
              severity="error"
              variant="default"
              size="small"
            />
          </S.ChipWrapper>
        )}

      <S.PlanSummaryMobile>
        <div>
          <S.DescriptionTitlePlan variant="sm">
            Plano {selectedPlanMobile?.name}
          </S.DescriptionTitlePlan>
          <Heading variant="sm">R$ {monetaryFormatter(totalValue)}</Heading>
        </div>

        <Button
          color="primary"
          onClick={onPaymentClick}
          size="medium"
          disabled={!selectedPlanMobile}
          data-ga="checkout_payment_modal_open_click"
        >
          Ir para pagamento
        </Button>
      </S.PlanSummaryMobile>
    </S.MobileCard>
  );
};

export default MobileFooterSummary;
